import get from 'lodash-es/get.js';
import { createSelector } from 'reselect';
import * as auth from '../auth';
import * as router from '../router';
import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as boardSelectors from '../board/selectors.js';
import * as boardSettingSelectors from '../board-setting/selectors.js';
import * as cardSelectors from '../card/selectors.js';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isUndefined from 'lodash-es/isUndefined';
import omitBy from 'lodash-es/omitBy';
import uniq from 'lodash-es/uniq';
import {
  getEndOfNextMonthTime,
  getEndOfNextWeekTime,
  getEndOfThisMonthTime,
  getEndOfThisWeekTime,
  getIdWoPrefix,
  getTodayTime,
  getTomorrowTime,
} from '../../utils';

/**
 * @param {Object} state Redux state
 * @returns Highlights data for current opened board.
 */
export const currentBoardHighlights = (state) => get(state, `board-highlights.${router.selectors.pageBoardId(state)}`);

/**
 * Current board hash Highlights or not.
 */
export const hasCurrentBoardHighlights = createSelector(
  (state) => currentBoardHighlights(state),
  (_currentBoardHighlights) => {
    return !isEmpty(_currentBoardHighlights);
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * @returns `true` when card matches board highlights criteria.
 */
export const isCardHighlighted = createSelector(
  (state, cardId) => firestoreRedux.selectors.doc(state, 'cards', cardId),
  currentBoardHighlights,
  auth.selectors.currentUserId,
  (state, cardId, boardId) => boardSettingSelectors.tagsEnabled(state, boardId),
  router.selectors.pageName,
  router.selectors.referrerPage,
  (state, cardId) => cardSelectors.summary(state, cardId),
  (card, highlights, currentUserId, tagsEnabled, pageName, referrerPage, summary) => {
    if (!highlights || pageName !== 'BOARD' || referrerPage === 'VIEWS') {
      return;
    }

    const isActive = card && card.columnType !== 'DONE' && card.columnType !== 'TRASH';

    // Overdue card or not.
    const dueDate = get(card, `dueDate`);
    const minDue = get(card, `minDue`);
    const curTime = Date.now();
    const overDue = isActive && dueDate && (curTime > dueDate || curTime > minDue);
    const status = get(card, `status`, 'NORMAL');

    switch (highlights.type) {
      case 'ASSIGNED_TO_ME':
        const assignments = get(card, `assignments`, []);
        return assignments.includes(currentUserId);

      case 'NEEDS_ATTENTION':
        const priority = get(card, 'priority');
        return (
          overDue ||
          priority == 'CRITICAL' ||
          priority === 'HIGH_PRIORITY' ||
          (isActive && (status === 'BLOCKED' || status === 'NEEDS_REVIEW' || status === 'NEEDS_REWORK' || status === 'ON_HOLD'))
        );

      case 'HIGH_PRIORITY_AND_CRITICAL':
        return get(card, `priority`) === 'HIGH_PRIORITY' || get(card, `priority`) === 'CRITICAL';

      case 'OVERDUE':
        return overDue;

      case 'CUSTOM':
        let assignmentMatched = false;
        let statusMatched = false;
        let dueDateMatched = false;
        let priorityMatched = false;
        let tagsMatched = false;

        // Assignments
        const cardAssignments = get(card, `assignments`, []);
        const selectedAssignments = get(highlights, `data.assignments`);
        if (
          !selectedAssignments ||
          selectedAssignments.includes('ANY') ||
          (selectedAssignments.includes('NO_ONE') && isEmpty(cardAssignments))
        ) {
          assignmentMatched = true;
        } else {
          if (!isEmpty(selectedAssignments)) {
            for (let id of selectedAssignments) {
              if (cardAssignments.includes(id)) {
                assignmentMatched = true;
                break;
              }
            }
          }
        }

        // Status
        // For status filtering, checks `status` (active cards) and `columnType` (Completed/Deleted cards).
        const selectedStatus = get(highlights, 'data.status');
        const columnType = get(card, 'columnType');

        if (!selectedStatus || selectedStatus.includes('ANY')) {
          statusMatched = true;
        } else if ((columnType && selectedStatus.includes(columnType)) || (isActive && selectedStatus.includes(status))) {
          statusMatched = true;
        }

        // Due Date
        const selectedDueDate = get(highlights, `data.dueDate`);
        if (!selectedDueDate || selectedDueDate.includes('ANY') || (isActive && !dueDate && selectedDueDate.includes('NOT_SCHEDULED'))) {
          dueDateMatched = true;
        } else {
          switch (selectedDueDate) {
            case 'OVERDUE':
              dueDateMatched = isActive && overDue;
              break;
            case 'TODAY':
              dueDateMatched = isActive && dueDate && dueDate > curTime && dueDate <= getTodayTime();
              break;
            case 'TOMORROW':
              dueDateMatched = isActive && dueDate && dueDate > getTodayTime() && dueDate <= getTomorrowTime();
              break;
            case 'THIS_WEEK':
              dueDateMatched = isActive && dueDate && dueDate > curTime && dueDate <= getEndOfThisWeekTime();
              break;
            case 'NEXT_WEEK':
              dueDateMatched = isActive && dueDate && dueDate > getEndOfThisWeekTime() && dueDate <= getEndOfNextWeekTime();
              break;
            case 'THIS_MONTH':
              dueDateMatched = isActive && dueDate && dueDate > curTime && dueDate <= getEndOfThisMonthTime();
              break;
            case 'NEXT_MONTH':
              dueDateMatched = isActive && dueDate && dueDate > getEndOfThisMonthTime() && dueDate <= getEndOfNextMonthTime();
              break;
          }
        }

        // Priority
        const cardPriority = get(card, `priority`, 'NORMAL');
        const selectedPriority = get(highlights, `data.priority`);
        if (!selectedPriority || selectedPriority.includes('ANY') || selectedPriority.includes(cardPriority)) {
          priorityMatched = true;
        }

        // Tags
        const cardTags = get(summary, `tags`, []) || [];
        const selectedTags = tagsEnabled ? get(highlights, `data.tags`) : ['ANY'];
        if (!selectedTags || selectedTags.includes('ANY') || (selectedTags.includes('WITHOUT_TAG') && isEmpty(cardTags))) {
          tagsMatched = true;
        } else {
          if (!isEmpty(selectedTags)) {
            for (let id of selectedTags) {
              if (cardTags && cardTags.includes(id)) {
                tagsMatched = true;
                break;
              }
            }
          }
        }

        return assignmentMatched && statusMatched && dueDateMatched && priorityMatched && tagsMatched;
    }
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/**
 * Removes the internal `ANY` sentinel from a multi-select highlights option.
 * `ANY` means "no constraint", which is expressed to the server as an omitted field.
 * `NO_ONE` / `WITHOUT_TAG` are explicit values and are passed through.
 *
 * @param {Array<String>} values - Selected option values
 * @returns {Array<String>|undefined} Values without `ANY`, or `undefined` when unconstrained
 */
const normalizeMultiSelect = (values) => {
  const filtered = (values || []).filter((value) => value !== 'ANY');
  return isEmpty(filtered) ? undefined : filtered;
};

/**
 * Returns the current board's highlights criteria in the shape expected by the AI server, or
 * `undefined` when no highlights filter is active.
 *
 * Only the criteria is sent; the server resolves which cards match. Internal `ANY` sentinels are
 * dropped so an unconstrained option is simply absent from the request.
 *
 * @param {Object} state - Redux state
 * @returns {Object|undefined} `{ type, data? }`
 */
export const highlightCriteria = createSelector(
  currentBoardHighlights,
  (state) => boardSettingSelectors.tagsEnabled(state, router.selectors.pageBoardId(state)),
  (highlights, tagsEnabled) => {
    const type = get(highlights, 'type');
    if (!type) {
      return;
    }

    if (type !== 'CUSTOM') {
      // The board's internal preset is HIGH_PRIORITY_AND_CRITICAL, but a card matches on high
      // priority OR critical — so the server expects HIGH_PRIORITY_OR_CRITICAL.
      return { type: type === 'HIGH_PRIORITY_AND_CRITICAL' ? 'HIGH_PRIORITY_OR_CRITICAL' : type };
    }

    const dueDate = get(highlights, 'data.dueDate');
    const data = {
      assignments: normalizeMultiSelect(get(highlights, 'data.assignments')),
      status: normalizeMultiSelect(get(highlights, 'data.status')),
      dueDate: !dueDate || dueDate === 'ANY' ? undefined : dueDate,
      priority: normalizeMultiSelect(get(highlights, 'data.priority')),
      // Tags are ignored by the board when the tags feature is off, so they aren't sent either.
      tags: tagsEnabled ? normalizeMultiSelect(get(highlights, 'data.tags')) : undefined,
    };

    return { type, data: omitBy(data, isUndefined) };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

const EMPTY_ARRAY = [];

const boardHighlightInputs = [
  (state) => boardSelectors.otherCards(state) || EMPTY_ARRAY,
  (state) => boardSelectors.doneCards(state) || EMPTY_ARRAY,
  (state) => boardSelectors.trashCards(state) || EMPTY_ARRAY,
  currentBoardHighlights,
  auth.selectors.currentUserId,
  (state) => boardSettingSelectors.tagsEnabled(state),
  router.selectors.pageName,
  router.selectors.referrerPage,
  (state) => firestoreRedux.selectors.collection(state, 'cards-summary'),
];

const highlightedCards = (activeCards, doneCards, trashCards, highlights, currentUserId, tagsEnabled, pageName, referrerPage, summaries) =>
  [...activeCards, ...doneCards, ...trashCards].filter((card) =>
    isCardHighlighted.resultFunc(
      card,
      highlights,
      currentUserId,
      tagsEnabled,
      pageName,
      referrerPage,
      get(summaries, `cs_${getIdWoPrefix({ id: card.id, prefix: 'crd_' })}`)
    )
  );

/**
 * Returns the IDs of all cards currently highlighted by the board highlights filter.
 * Returns an empty array when no highlights filter is active or no cards match.
 *
 * Includes active, done, and trash cards, as the board renders each of them highlighted when it matches.
 * Only overdue and status checks are limited to active cards; ASSIGNED_TO_ME and
 * HIGH_PRIORITY_AND_CRITICAL also match done and trash cards.
 *
 * @param {Object} state - Redux state
 * @returns {Array<String>} Highlighted card IDs
 */
export const highlightedCardIds = createSelector(
  boardHighlightInputs,
  (...args) => highlightedCards(...args).map((card) => card.id),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  }
);

/**
 * @param {Object} state - Redux state
 * @returns {Array<String>} ids of the current board's columns holding a card that matches the active Highlights, hidden columns included.
 */
export const highlightedColumnIds = createSelector(
  boardHighlightInputs,
  (...args) => uniq(highlightedCards(...args).map((card) => card.columnId)),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
    },
  }
);
